/**
 * 어시스턴트 대화 상태 — 스레드 목록·메시지·스트리밍 버퍼·열린 질문.
 *
 * 서버가 진실이고(메시지는 SQLite 에 남는다) 이 훅은 화면용 사본을 들고 있는다.
 * SSE 이벤트를 그대로 메시지 배열에 접어 넣어, 새로고침 후 `GET /threads/:id` 로 복원한
 * 모양과 같아지게 한다.
 *
 * 원본: LiveSQL `hooks/useAssistantThread.js`. 여기로 올리면서 **도메인 결합을 걷어냈다**:
 *   · `connectionId`/`schema`/`editorSelection` → 호출자가 주는 불투명한 `context`
 *   · `sql_proposal` → 서비스가 실제로 보내는 `proposal` (payload 는 해석하지 않는다)
 *   · 모듈 import 하던 클라이언트 → 인자로 주입
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantClient, AssistantAccount, AssistantThread, AssistantMessage } from './client';

/** 화면이 다루는 메시지 — 저장 메시지와 스트림 이벤트가 같은 모양으로 접힌다. */
export interface UiMessage {
  id: string;
  seq?: number;
  role: 'user' | 'assistant' | 'tool' | 'system';
  kind: string;
  content?: string;
  /** kind==='question' */
  question?: any;
  resolved?: { answer: any; source: string } | null;
  /** kind==='proposal' — 도메인 산출물. 훅은 들여다보지 않는다 */
  artifact?: any;
  rationale?: string;
  /** kind==='tool_call' */
  tool?: string;
  input?: any;
  result?: any;
  isError?: boolean;
  toolUseId?: string;
  /** 낙관적 추가분 표시 (turn_start 에서 서버 id 로 교체된다) */
  pending?: boolean;
  code?: string;
}

export interface UseAssistantThreadOptions {
  client: AssistantClient;
  /** 패널이 열려 있는가 — false 면 계정·목록을 읽지 않는다 */
  enabled: boolean;
  /** 턴마다 서버로 보낼 도메인 컨텍스트. 훅은 내용을 해석하지 않는다 */
  getContext?: () => any;
  provider?: string;
  model?: string;
  /** 제안(proposal) 이 오면 호출 — 도메인 화면이 받아 처리한다 */
  onProposal?: (msg: UiMessage) => void;
}

/** 서버 저장 메시지(GET /threads/:id) → 화면 메시지 */
function fromStored(m: AssistantMessage): UiMessage {
  const base: UiMessage = { id: m.id, seq: m.seq, role: m.role, kind: m.kind, content: m.content };
  if (m.kind === 'question') {
    return { ...base, question: m.payload || {}, resolved: (m.payload && m.payload.resolved) || null };
  }
  if (m.kind === 'proposal') {
    return { ...base, artifact: m.payload && m.payload.artifact, rationale: (m.payload && m.payload.rationale) || '' };
  }
  // LiveSQL 자체 백엔드는 `sql_proposal` 로 저장하고 산출물이 content 에 있다 (payload 아님).
  if (m.kind === 'sql_proposal') {
    return { ...base, kind: 'proposal', artifact: m.content, rationale: (m.payload && m.payload.rationale) || '' };
  }
  if (m.kind === 'tool_call') {
    return {
      ...base,
      tool: m.content,
      input: m.payload && m.payload.input,
      result: m.payload && m.payload.result,
      isError: !!(m.payload && m.payload.isError),
    };
  }
  return base;
}

let localSeq = 100000;
const nextLocalId = () => `local_${++localSeq}`;

export function useAssistantThread(opts: UseAssistantThreadOptions) {
  const { client, enabled, getContext, provider = 'claude', model, onProposal } = opts;

  const [threads, setThreads] = useState<AssistantThread[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [streamText, setStreamText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<AssistantAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const threadIdRef = useRef<string | null>(null);
  threadIdRef.current = threadId;

  // 콜백을 ref 로 잡아 둔다 — 매 렌더 새 함수가 와도 send/handleEvent 가 재생성되지 않게.
  const getContextRef = useRef(getContext);
  getContextRef.current = getContext;
  const onProposalRef = useRef(onProposal);
  onProposalRef.current = onProposal;

  const refreshAccount = useCallback(async () => {
    try { setAccount(await client.getAccount(provider)); }
    catch (err: any) { setAccount(null); setError(err.message); }
  }, [client, provider]);

  const refreshThreads = useCallback(async () => {
    try {
      const list = (await client.listThreads()) || [];
      setThreads(list);
      return list;
    } catch (err: any) { setError(err.message); return []; }
  }, [client]);

  const openThread = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const r = await client.getThread(id);
      setThreadId(id);
      setMessages((r.messages || []).map(fromStored));
      setStreamText('');
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally { setLoading(false); }
  }, [client]);

  const newThread = useCallback(async () => {
    setLoading(true);
    try {
      const thread = await client.createThread({
        provider,
        model: model || undefined,
        context: getContextRef.current ? getContextRef.current() : undefined,
      });
      setThreadId(thread.id);
      setMessages([]);
      setStreamText('');
      setError(null);
      await refreshThreads();
      return thread.id;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally { setLoading(false); }
  }, [client, provider, model, refreshThreads]);

  const removeThread = useCallback(async (id: string) => {
    try {
      await client.deleteThread(id);
      if (threadIdRef.current === id) { setThreadId(null); setMessages([]); }
      await refreshThreads();
    } catch (err: any) { setError(err.message); }
  }, [client, refreshThreads]);

  const push = useCallback(
    (msg: Partial<UiMessage>) => setMessages(prev => [...prev, { id: nextLocalId(), ...msg } as UiMessage]),
    [],
  );

  const handleEvent = useCallback((type: string, data: any) => {
    switch (type) {
      case 'turn_start':
        setMessages(prev => prev.map(m => (m.pending ? { ...m, id: data.userMessageId || m.id, pending: false } : m)));
        break;
      case 'text_delta':
        setStreamText(prev => prev + (data.text || ''));
        break;
      case 'text':
        setStreamText('');
        push({ id: data.messageId, role: 'assistant', kind: 'text', content: data.text });
        break;
      case 'thinking':
        // 표시는 화면 몫 — 훅은 흘려보낸다(메시지로 접지 않는다)
        break;
      case 'tool_call':
        push({ id: data.messageId, role: 'tool', kind: 'tool_call', tool: data.name, input: data.input, toolUseId: data.id });
        break;
      case 'tool_result':
        setMessages(prev => prev.map(m => (m.toolUseId === data.id ? { ...m, result: data.preview, isError: !data.ok } : m)));
        break;
      case 'question':
        push({ id: data.messageId, role: 'assistant', kind: 'question', question: data, resolved: null });
        break;
      case 'question_resolved':
        setMessages(prev => prev.map(m => (
          m.kind === 'question' && m.question && m.question.questionId === data.questionId
            ? { ...m, resolved: { answer: data.answer, source: data.source } }
            : m
        )));
        break;
      // `sql_proposal` 은 LiveSQL 자체 백엔드가 쓰는 옛 이름이다. 산출물이 `sql` 필드로 온다.
      // 공통 서비스는 `proposal` + `artifact` 를 쓴다. 훅은 둘 다 받아 같은 모양으로 접는다.
      case 'proposal':
      case 'sql_proposal': {
        const msg: UiMessage = {
          id: data.messageId || nextLocalId(),
          role: 'assistant',
          kind: 'proposal',
          artifact: type === 'sql_proposal' ? data.sql : data.artifact,
          rationale: data.rationale,
          content: type === 'sql_proposal' ? data.sql : (data.tool || ''),
        };
        setMessages(prev => [...prev, msg]);
        if (onProposalRef.current) onProposalRef.current(msg);
        break;
      }
      case 'error':
        push({ role: 'system', kind: 'error', content: data.message, code: data.code });
        break;
      case 'done':
        setStreamText('');
        refreshThreads();
        break;
      default:
        break;
    }
  }, [push, refreshThreads]);

  const send = useCallback(async (text: string) => {
    if (!text.trim() || busy) return;
    let id = threadIdRef.current;
    if (!id) { id = await newThread(); if (!id) return; }
    setError(null);
    setBusy(true);
    setStreamText('');
    push({ role: 'user', kind: 'text', content: text, pending: true });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await client.runTurn(id, text, {
        context: getContextRef.current ? getContextRef.current() : {},
        signal: controller.signal,
        onEvent: (ev) => handleEvent(ev.type, ev.data),
      });
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        push({ role: 'system', kind: 'error', content: err.message, code: err.errorType });
      }
    } finally {
      setBusy(false);
      setStreamText('');
      abortRef.current = null;
    }
  }, [busy, client, newThread, push, handleEvent]);

  const answer = useCallback(async (questionId: string, value: string | string[]) => {
    const id = threadIdRef.current;
    if (!id) return;
    try {
      await client.answerQuestion(id, questionId, value);
      setMessages(prev => prev.map(m => (
        m.kind === 'question' && m.question && m.question.questionId === questionId
          ? { ...m, resolved: { answer: value, source: 'user' } }
          : m
      )));
    } catch (err: any) {
      setError(err.message);
    }
  }, [client]);

  const stop = useCallback(async () => {
    const id = threadIdRef.current;
    if (id) { try { await client.abortTurn(id); } catch (_e) { /* 아래에서 로컬 중단 */ } }
    if (abortRef.current) abortRef.current.abort();
  }, [client]);

  // 패널을 처음 열 때 계정·스레드 목록을 읽는다.
  useEffect(() => {
    if (!enabled) return;
    refreshAccount();
    refreshThreads();
  }, [enabled, refreshAccount, refreshThreads]);

  // 언마운트하면 진행 중 스트림을 끊는다 (서버 턴은 abort 로 정리된다).
  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const openQuestion = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.kind === 'question') return m.resolved ? null : m;
    }
    return null;
  })();

  return {
    threads, threadId, messages, streamText, busy, error, account, loading, openQuestion,
    setError, refreshAccount, refreshThreads, openThread, newThread, removeThread, send, answer, stop,
  };
}

export default useAssistantThread;
